<template>
  
  <report-panel icon="$reservation" :title="$t('titles.transactions_and_average_daily_rate_by_weekday')" :loading="loading">

    <!-- <template v-slot:tools> -->

      <v-row>
        <v-col class="text-end mb-5">
          <!-- TODO: make into a component -->
          <v-btn-toggle
            v-model="formState.mode"
            rounded="0"
            group
            divided
            variant="outlined"
            mandatory
            density="compact"
          >
            <v-btn v-for="modeOption in modeOptions" :value="modeOption.value">
              <span class="d-sm-none">{{ modeOption.mobileTitle }}</span>
              <span class="d-none d-sm-inline">{{ modeOption.title }}</span>
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

    <!-- </template> -->

    <div ref="chartContainer">
      <bar
        :chart-options="chartOptions"
        :chart-data="chartData"
        :styles="chartStyles"
      />
    </div>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, reactive, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import ReportPanel from './ReportPanel.vue'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'

// ChartJS
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  // Title,
  Tooltip,
  Legend,
  LineElement,
  LineController,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale, type ChartOptions, type ChartData, type TooltipItem, type ChartDataset,
} from 'chart.js'
import type {
  ReportingDateSummaries,
  ReportingDateSummaryCountBreakdown, ReportingDateSummaryDollarAmountBreakdown
} from '../../../../services/api-clients/ReportingApi'
ChartJS.register(
  Tooltip,
  Legend,
  LineElement,
  LineController,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale
)

interface Props {
  loading: boolean
  dateSummaries: ReportingDateSummaries
  channels: string[]
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  channels: () => [],
})

const { n, t } = useI18n()
const themeColors = useTheme().current.value.colors

/**
 *
 */

interface FormState {
  mode: 'transaction_date' | 'start_date'
}

const formState = reactive<FormState>({
  mode: 'transaction_date',
})

const modeOptions = [
  {value: 'start_date', title: t('titles.start_date'), mobileTitle: t('titles.start_date_short')},
  {value: 'transaction_date', title: t('titles.transaction_date'), mobileTitle: t('titles.transaction_date_short')},
]

/**
 * Chart
 */

const chartOptions: ChartOptions<'bar'> = {
  plugins: {
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'bar'> ): string => {
          const dataPoint = context.raw as number
          let formatted
          if (context.dataset.yAxisID === 'yAdr') {
            formatted = n(dataPoint, 'currency')
          } else {
            formatted = dataPoint.toString()
          }
          return `${context.dataset.label}: ${formatted}`
        },
      },
    },
  },
  scales: {
    yTransactions: {
      axis: 'y',
      type: 'linear',
      position: 'left',
    },
    yAdr: {
      axis: 'y',
      type: 'linear',
      position: 'right',
      ticks: {
        callback: (value, _index, _values) => {
          return n(value as number, {style: 'currency', currency: 'USD', maximumFractionDigits: 0})
        },
      },
    },
  },
}

interface Totals {
  revenue: number
  duration: number
  transactions: number
  days: number
}

const chartData = computed<ChartData<'bar', number[]>>(() => {
  
  const totals: Totals[] = [
    {revenue: 0, duration: 0, transactions: 0, days: 0},
    {revenue: 0, duration: 0, transactions: 0, days: 0},
    {revenue: 0, duration: 0, transactions: 0, days: 0},
    {revenue: 0, duration: 0, transactions: 0, days: 0},
    {revenue: 0, duration: 0, transactions: 0, days: 0},
    {revenue: 0, duration: 0, transactions: 0, days: 0},
    {revenue: 0, duration: 0, transactions: 0, days: 0},
  ]

  Object.entries(props.dateSummaries).forEach(([dateString, summary]) => {

    // ES6 says that Sunday = 0. We say that Monday = 0.
    const es6Weekday = new Date(dateString).getUTCDay()
    const ourWeekday = es6Weekday === 0 ? 6 : es6Weekday - 1

    totals[ourWeekday].days++

    let transactions: ReportingDateSummaryCountBreakdown | null = null
    let grossRevenue: ReportingDateSummaryDollarAmountBreakdown | null = null
    let duration: ReportingDateSummaryCountBreakdown | null = null

    switch (formState.mode) {
      case 'start_date':
        transactions = summary.as_start_date.transactions
        grossRevenue = summary.as_start_date.gross_revenue
        duration = summary.as_start_date.duration ?? null
        break
      case 'transaction_date':
        transactions = summary.as_transaction_date.transactions
        grossRevenue = summary.as_transaction_date.gross_revenue
        duration = summary.as_transaction_date.duration ?? null
        break
      default:
        throw new Error(`unrecognized mode: "${formState.mode}"`)
    }

    Object.entries(transactions.by_channel).forEach(([channel, transactions]) => {
      if (!props.channels.length || props.channels.includes(channel)) {
        totals[ourWeekday].transactions += transactions
      }
    })

    Object.entries(grossRevenue.by_channel).forEach(([channel, grossRevenue]) => {
      if (!props.channels.length || props.channels.includes(channel)) {
        totals[ourWeekday].revenue += grossRevenue
      }
    })

    if (duration) {
      Object.entries(duration.by_channel).forEach(([channel, duration]) => {
        if (!props.channels.length || props.channels.includes(channel)) {
          totals[ourWeekday].duration += duration
        }
      })
    }

  })

  const adrDataset: ChartDataset<'line'> = {
    label: t('titles.average_daily_rate'),
    type: 'line',
    order: 1,
    backgroundColor: themeColors.secondary,
    yAxisID: 'yAdr',
    data: totals.map(item => {
      if (!item.duration) {
        return 0
      }
      const durationInDays = item.duration / 86400
      return item.revenue / durationInDays // TODO: is this right?
    }),
  }

  const transactionsDataset: ChartDataset<'bar'> = {
    label: t('titles.transactions'),
    type: 'bar',
    order: 2,
    yAxisID: 'yTransactions',
    backgroundColor: themeColors.success,
    data: totals.map(item => {
      return item.days > 0 ? item.transactions / item.days : 0
    }),
  }

  const data: ChartData<'bar'> = {
    labels: [
      t('enums.weekdays_abbr.mon'),
      t('enums.weekdays_abbr.tue'),
      t('enums.weekdays_abbr.wed'),
      t('enums.weekdays_abbr.thu'),
      t('enums.weekdays_abbr.fri'),
      t('enums.weekdays_abbr.sat'),
      t('enums.weekdays_abbr.sun'),
    ],
    // @ts-ignore: don't complain that we're populating a line dataset into a bar chart
    datasets: [adrDataset, transactionsDataset],
  }

  return data

})

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const container = useTemplateRef('chartContainer')
const { chartStyles } = useFitChartToContainer(container)

</script>
